/**
 * Relevance matching for navigation page links.
 *
 * The old matcher was a strict AND filter over a single flattened string: every
 * typed word had to appear in some field or the page was dropped, and the field
 * list accidentally omitted the page's own name. That made whole pages
 * unreachable ("Studio Settings" lives at /workspaces/<id>/edit, so nothing in
 * its URL or section spells "settings").
 *
 * This replaces the filter with a score. Each typed term is matched against
 * every field independently, keeping its best hit; fields carry different
 * weights so a name match beats a URL-slug match. Terms still have to land
 * somewhere — a page nobody asked for should not surface — but "landing" now
 * includes prefixes, substrings, initialisms, and small typos.
 */

// Words people type to describe the act of navigating rather than the
// destination. Dropping them keeps "open studio settings" as precise as
// "studio settings" instead of failing on the verb.
const NAVIGATION_FILLER_WORDS = new Set([
  "go",
  "goto",
  // "show me the …" — no destination is named "me", and left in it is a
  // two-letter term that prefix-matches half the menu.
  "me",
  "menu",
  "nav",
  "navigate",
  "open",
  "page",
  "screen",
  "show",
  "take",
  "the",
  "to",
  "view",
  "where",
]);

// A name match should outrank a URL match by enough that no pile-up of weak
// field hits can overtake it.
const FIELD_WEIGHTS = {
  title: 10,
  heading: 9,
  // What the page is also called outranks what the page is merely about: a
  // page nicknamed "faq" should beat one that lists "faq" among its topics.
  aliases: 7,
  keywords: 5,
  description: 4,
  section: 3,
  path: 2,
};

const FIELD_KEYS = Object.keys(FIELD_WEIGHTS);

// Phrase bonuses dwarf per-term scores: typing a page's full name should pin it
// to the top no matter how many other pages share a word with it.
const PHRASE_BONUS = {
  titleExact: 120,
  titlePrefix: 60,
  titleContains: 25,
  headingExact: 100,
  headingPrefix: 45,
  headingContains: 20,
};

// Floor for the relaxed pass, roughly "one term matched a name well". Below it
// the hit is a stray substring and showing it is worse than showing nothing.
const RELAXED_MIN_SCORE = 7;

// Record ids in a URL are noise to a human searching for a page. Detect them by
// shape rather than by id scheme: any opaque run of 16+ alphanumerics, or a
// UUID. The longest word in a hand-written route segment is "conversation" (12),
// so this cannot swallow a real slug, and it keeps working if cuid gives way to
// uuid or nanoid.
const OPAQUE_ID = /^[a-z0-9]{16,}$/i;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isOpaqueIdSegment(segment) {
  const value = String(segment ?? "");
  return OPAQUE_ID.test(value) || UUID.test(value);
}

export function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function tokenizeText(value) {
  return normalizeText(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Typos are only tolerated once a term is long enough that an edit is more
 * likely a slip than a different word ("cal" should not reach "call").
 */
function fuzzyTolerance(term) {
  if (term.length >= 8) return 2;
  if (term.length >= 5) return 1;
  return 0;
}

/** Bounded Levenshtein — returns true when `a` and `b` are within `max` edits. */
function isWithinEditDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return false;
  if (a === b) return true;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + cost,
      );
      if (current[j] < rowBest) rowBest = current[j];
    }
    // Every later row can only grow, so bail as soon as the whole row is out.
    if (rowBest > max) return false;
    previous = current;
  }
  return previous[b.length] <= max;
}

function buildField(value) {
  const text = Array.isArray(value)
    ? normalizeText(value.filter(Boolean).join(" "))
    : normalizeText(value);
  return { text, words: tokenizeText(text) };
}

function buildPathField(href) {
  // URL slugs are the weakest signal, but they are the only thing that spells
  // out routes with no prose ("/workspaces/admin/fbiv"). Ids are noise.
  //
  // Drop the ids a segment at a time, before tokenising rather than after:
  // tokenizeText splits on every non-alphanumeric, so a hyphenated uuid would
  // reach the filter as five fragments, each too short to look like an id. The
  // longest hand-written segment is "leaderboard" (11), well clear of the
  // 16-character threshold.
  const words = String(href || "")
    .replace(/[?#].*$/, "")
    .split("/")
    .filter((segment) => segment && !isOpaqueIdSegment(segment))
    .flatMap((segment) => tokenizeText(segment))
    .filter((word) => !/^\d+$/.test(word));
  return { text: words.join(" "), words };
}

function getPageFields(page) {
  return {
    title: buildField(page.title || page.label),
    heading: buildField(page.heading),
    aliases: buildField(page.aliases),
    keywords: buildField(page.keywords),
    description: buildField(page.description),
    section: buildField(page.section),
    path: buildPathField(page.href),
  };
}

/** Best quality (0–1) with which a single term matches one field. */
function scoreTermAgainstField(term, field) {
  if (!field.text) return 0;
  if (field.text === term) return 1;
  if (field.words.includes(term)) return 1;
  if (field.text.startsWith(term)) return 0.92;

  let best = 0;
  const tolerance = fuzzyTolerance(term);
  for (const word of field.words) {
    if (word.startsWith(term)) {
      best = Math.max(best, 0.85);
      continue;
    }
    if (word.includes(term)) {
      best = Math.max(best, 0.6);
      continue;
    }
    if (tolerance > 0 && isWithinEditDistance(word, term, tolerance)) {
      best = Math.max(best, 0.5);
    }
  }

  // Initialisms ("cm" → Capability Maps) only when nothing better landed, so
  // they never outrank a real word match.
  if (best === 0 && term.length >= 2 && field.words.length > 1) {
    const initials = field.words.map((word) => word[0]).join("");
    if (initials === term) best = 0.8;
    else if (initials.startsWith(term)) best = 0.65;
  }

  return best;
}

export function parseSearchQuery(query) {
  const normalized = normalizeText(query);
  const allTerms = tokenizeText(normalized);
  const meaningful = allTerms.filter(
    (term) => !NAVIGATION_FILLER_WORDS.has(term),
  );
  // A query made entirely of filler ("open the page") has nothing to match on;
  // fall back to the raw tokens rather than matching everything.
  const terms = meaningful.length > 0 ? meaningful : allTerms;

  return {
    normalized,
    terms,
    // The phrase bonus has to survive a leading verb too, or "open studio
    // settings" would rank differently from "studio settings" — the exact
    // difference the filler list exists to erase.
    phrase: terms.join(" "),
  };
}

/** Best phrase bonus a field earns across the phrasings of one query. */
function phraseBonus(fieldText, phrases, bonuses) {
  let best = 0;
  for (const phrase of phrases) {
    if (fieldText === phrase) best = Math.max(best, bonuses.exact);
    else if (fieldText.startsWith(phrase)) best = Math.max(best, bonuses.prefix);
    else if (fieldText.includes(phrase)) best = Math.max(best, bonuses.contains);
  }
  return best;
}

export function scorePage(page, parsedQuery) {
  const { terms, normalized, phrase } = parsedQuery;
  if (terms.length === 0) return { score: 0, matchedTerms: 0 };

  const fields = getPageFields(page);
  let score = 0;
  let matchedTerms = 0;

  for (const term of terms) {
    let bestForTerm = 0;
    for (const key of FIELD_KEYS) {
      const quality = scoreTermAgainstField(term, fields[key]);
      if (quality > 0) {
        bestForTerm = Math.max(bestForTerm, quality * FIELD_WEIGHTS[key]);
      }
    }
    if (bestForTerm > 0) matchedTerms += 1;
    score += bestForTerm;
  }

  const phrases = [...new Set([normalized, phrase].filter(Boolean))];
  score += phraseBonus(fields.title.text, phrases, {
    exact: PHRASE_BONUS.titleExact,
    prefix: PHRASE_BONUS.titlePrefix,
    contains: PHRASE_BONUS.titleContains,
  });
  score += phraseBonus(fields.heading.text, phrases, {
    exact: PHRASE_BONUS.headingExact,
    prefix: PHRASE_BONUS.headingPrefix,
    contains: PHRASE_BONUS.headingContains,
  });

  return { score, matchedTerms };
}

/**
 * Rank pages against a query. Pages matching every term win; if none do, the
 * best partial matches are offered rather than an empty result, because a near
 * miss is still a usable link and "No matches found" is a dead end.
 */
export function matchPages(pages = [], query, limit = 8) {
  const parsed = parseSearchQuery(query);
  if (parsed.terms.length === 0) {
    // Nothing typed at all is a browse: offer the top of the menu. Something
    // typed that yields no terms (punctuation) asked for something specific
    // that does not exist, so answer with nothing rather than a random slice.
    if (parsed.normalized) return [];
    return pages.slice(0, limit).map((page) => ({ ...page, matchScore: 0 }));
  }

  const scored = [];
  for (const page of pages) {
    const result = scorePage(page, parsed);
    if (result.score > 0) scored.push({ page, ...result });
  }

  const complete = scored.filter(
    (entry) => entry.matchedTerms === parsed.terms.length,
  );
  const minPartialTerms = Math.max(1, Math.ceil(parsed.terms.length / 2));
  const pool =
    complete.length > 0
      ? complete
      : scored.filter(
          (entry) =>
            entry.matchedTerms >= minPartialTerms &&
            entry.score >= RELAXED_MIN_SCORE,
        );

  return pool
    .sort(
      (a, b) =>
        b.score - a.score ||
        String(a.page.title || "").localeCompare(String(b.page.title || "")),
    )
    .slice(0, limit)
    .map((entry) => ({ ...entry.page, matchScore: Math.round(entry.score) }));
}
